import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Html5QrcodeScanner } from 'html5-qrcode';
import axiosInstance from '../../../../../api';

const TransferCaseModal = ({ show, onHide, mode = 'add', transferCaseData, refreshList }) => {
  const title = mode === 'add' ? 'Add Transfer Case' : 'Edit Transfer Case';

  // States
  const [medicalCenters, setMedicalCenters] = useState([]);
  const [transferFrom, setTransferFrom] = useState('');
  const [transferTo, setTransferTo] = useState('');
  const [filteredTransferTo, setFilteredTransferTo] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [fileInputs, setFileInputs] = useState([0]);
  const [existingDocuments, setExistingDocuments] = useState({});
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    diagnose: '',
    bracelet_id: '',
    doctor: '',
    doctor_name: '',
    age: '',
    gender: '',
    documents: {},
    clinic_no: '',
    request_details: '',
    escort_team: {
      escort_physician: '',
      escort_nurse: '',
      escort_paramedic: '',
      other: ''
    }
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [userRole, setUserRole] = useState('');
  const [medicalCenterId, setMedicalCenterId] = useState(null);
  const [showScanner, setShowScanner] = useState(false);

  const MAX_FILES = 3;

  // Fetch full case data in edit mode
  useEffect(() => {
    if (!show) return;

    if (mode === 'edit' && transferCaseData?.id) {
      const fetchFullCase = async () => {
        try {
          setIsLoading(true);
          const res = await axiosInstance.get(`/ems/cases/${transferCaseData.id}`, {
            headers: {
              Accept: 'application/vnd.api+json',
              Authorization: `Bearer ${sessionStorage.getItem('auth_token') || sessionStorage.getItem('authToken')}`
            }
          });

          const data = res.data;
          const ps = data.patient_situation || {};

          setFormData({
            id: data.id,
            name: data.case_name || '',
            age: data.age || '',
            gender: data.gender || '',
            bracelet_id: data.bracelet_id || '',
            diagnose: data.diagnose || '',
            doctor: data.doctor?.id?.toString() || '',
            doctor_name: data.doctor_name || '',
            clinic_no: ps.clinic_no || '',
            request_details: ps.request_details || '',
            escort_team: {
              escort_physician: ps.escort_team?.escort_physician || '',
              escort_nurse: ps.escort_team?.escort_nurse || '',
              escort_paramedic: ps.escort_team?.escort_paramedic || '',
              other: ps.escort_team?.other || ''
            },
            documents: {}
          });

          setTransferFrom(String(data.transfer_from_medical_center || ''));
          setTransferTo(String(data.nearest_medical_center || ''));

          const docs = {};
          if (data.documents1) docs.documents1 = data.documents1;
          if (data.documents2) docs.documents2 = data.documents2;
          if (data.documents3) docs.documents3 = data.documents3;
          setExistingDocuments(docs);

          const docCount = Object.keys(docs).length;
          setFileInputs(docCount > 0 ? Array.from({ length: Math.max(docCount, 1) }, (_, i) => i) : [0]);
        } catch (err) {
          console.error('Failed to load case:', err);
          alert('Could not load case details');
        } finally {
          setIsLoading(false);
        }
      };
      fetchFullCase();
    } else if (mode === 'add') {
  const currentUserId = sessionStorage.getItem('user_id');

  setFormData(prev => ({
    ...prev,
    id: '',
    name: '',
    diagnose: '',
    bracelet_id: '',
    doctor: '',
    doctor_name: '',
    age: '',
    gender: '',
    documents: {},
    clinic_no: '',
    request_details: '',
    escort_team: { 
      escort_physician: '', 
      escort_nurse: '', 
      escort_paramedic: '', 
      other: '' 
    },
    user_id: currentUserId || ''   // This is the key fix!
  }));

  setTransferFrom('');
  setTransferTo('');
  setExistingDocuments({});
  setFileInputs([0]);
}
    setErrors({});
  }, [show, mode, transferCaseData]);

  // Load medical centers & user role
  useEffect(() => {
    if (!show) return;

    const role = sessionStorage.getItem('Role_Name');
    const medCenterId = sessionStorage.getItem('Medical_Center_Id');
    setUserRole(role || '');
    setMedicalCenterId(medCenterId || null);

    const fetchMedicalCenters = async () => {
      try {
        const res = await axiosInstance.get('/ems/medical-centers');
        const centers = Array.isArray(res.data) ? res.data : [];
        setMedicalCenters(centers);

        if ((role === 'Hospital Representative (Nurse)' || role === 'EMS Resource Manager') && medCenterId && medCenterId !== '0' && mode === 'add') {
          const userCenter = centers.find(c => c.id === parseInt(medCenterId));
          if (userCenter) {
            setTransferFrom(String(userCenter.id));
            setFilteredTransferTo(centers.filter(c => c.id !== userCenter.id));
          }
        } else {
          setFilteredTransferTo(centers);
        }
      } catch (err) {
        console.error('Failed to load medical centers', err);
      }
    };
    fetchMedicalCenters();
  }, [show, mode]);

  // Update filtered transfer to
  useEffect(() => {
    if (transferFrom && medicalCenters.length > 0) {
      setFilteredTransferTo(medicalCenters.filter(c => c.id !== parseInt(transferFrom)));
    }
  }, [transferFrom, medicalCenters]);

  // Fetch doctors
  useEffect(() => {
    if (!transferFrom) {
      setDoctors([]);
      return;
    }
    const fetchDoctors = async () => {
      try {
        const res = await axiosInstance.get(`/ems/get-specefic-medical-center-doctors-list/${transferFrom}`);
        const list = Array.isArray(res.data) ? res.data : [];
        const mapped = list.map(d => ({ id: d.id, name: `${d.first_name} ${d.last_name}`.trim() }));
        setDoctors(mapped);

        if (formData.doctor) {
          const matched = mapped.find(d => d.id === parseInt(formData.doctor));
          if (matched) {
            setFormData(prev => ({ ...prev, doctor_name: matched.name }));
          }
        }
      } catch (err) {
        setDoctors([]);
      }
    };
    fetchDoctors();
  }, [transferFrom, formData.doctor]);

  // QR Scanner
  useEffect(() => {
    if (!showScanner) return;
    const scanner = new Html5QrcodeScanner('reader', { fps: 10, qrbox: 250 }, false);
    scanner.render(
      (text) => {
        setFormData(prev => ({ ...prev, bracelet_id: text }));
        setShowScanner(false);
        scanner.clear();
      },
      () => { }
    );
    return () => scanner.clear();
  }, [showScanner]);

  // Handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'name') {
      if (/^[a-zA-Z\s]*$/.test(value)) {
        setFormData(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, name: '' }));
      } else {
        setErrors(prev => ({ ...prev, name: 'Name can only contain letters and spaces' }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleEscortTeamChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      escort_team: { ...prev.escort_team, [name]: value }
    }));
  };

  const handleDoctorChange = (e) => {
    const id = e.target.value;
    const doctor = doctors.find(d => d.id === parseInt(id));
    setFormData(prev => ({
      ...prev,
      doctor: id,
      doctor_name: doctor ? doctor.name : ''
    }));
    setErrors(prev => ({ ...prev, doctor: '' }));
  };

  const handleFileChange = (e, index) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jfif', 'application/pdf'];
    const maxSize = 5 * 1024 * 1024;

    if (!validTypes.includes(file.type)) {
      setErrors(prev => ({ ...prev, [`documents${index + 1}`]: 'Only PDF, JPG, PNG, WEBP allowed' }));
      return;
    }
    if (file.size > maxSize) {
      setErrors(prev => ({ ...prev, [`documents${index + 1}`]: 'File must be under 5MB' }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      documents: { ...prev.documents, [`documents${index + 1}`]: file }
    }));
    setErrors(prev => ({ ...prev, [`documents${index + 1}`]: '', documents: '' }));
  };

  const addFileInput = () => fileInputs.length < MAX_FILES && setFileInputs([...fileInputs, fileInputs.length]);
  const removeFileInput = (index) => {
    const key = `documents${index + 1}`;
    setFileInputs(fileInputs.filter((_, i) => i !== index));
    setFormData(prev => {
      const { [key]: _, ...rest } = prev.documents;
      return { ...prev, documents: rest };
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    else if (!/^[a-zA-Z\s]+$/.test(formData.name.trim())) newErrors.name = 'Name can only contain letters and spaces';
    if (!transferFrom) newErrors.transferFrom = 'Transfer From is required';
    if (!transferTo) newErrors.transferTo = 'Transfer To is required';
    if (!formData.diagnose.trim()) newErrors.diagnose = 'Diagnose is required';
    if (!formData.doctor) newErrors.doctor = 'Please select a doctor';
    if (!formData.clinic_no.trim()) newErrors.clinic_no = 'Clinic No is required';
    if (!formData.request_details.trim()) newErrors.request_details = 'Request details are required';

    if (mode === 'add') {
      if (!formData.bracelet_id.trim()) newErrors.bracelet_id = 'Bracelet ID is required';
      if (Object.keys(formData.documents).length === 0 && Object.keys(existingDocuments).length === 0) {
        newErrors.documents = 'At least one attachment is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  if (!validateForm()) return;

  setIsLoading(true);
  try {
    const documentsBase64 = {};
    for (const [key, file] of Object.entries(formData.documents)) {
      if (file instanceof File) {
        documentsBase64[key] = await fileToBase64(file);
      }
    }

    // Base payload matching your working cURL
    const payload = {
      user_id: formData.user_id || sessionStorage.getItem('user_id'),
      case_name: formData.name.trim(),
      age: formData.age ? parseInt(formData.age) : null,
      gender: formData.gender || null,
      nearest_medical_center: parseInt(transferTo),
      transfer_from_medical_center: parseInt(transferFrom),
      diagnose: formData.diagnose.trim(),
      bracelet_id: formData.bracelet_id.trim(),
      doctor: formData.doctor ? parseInt(formData.doctor) : null,
      doctor_name: formData.doctor_name || null,
      clinic_no: formData.clinic_no.trim(),
      request_details: formData.request_details.trim(),
      escort_team: formData.escort_team,

      // Required fields from your working example
      triage_card: "Red",                    // You can make this dynamic later
      case_status: 2,                         // Pending or whatever default
      nurse: null,                            // Or get from logged-in user
      nurse_name: null,                       // Or get from session
      original_case_id: null,
      doctor_approval_status: 0,

      // Handle documents: send new ones, clear removed ones
      documents1: documentsBase64.documents1 || (existingDocuments.documents1 && !formData.documents.documents1 ? "" : existingDocuments.documents1 || ""),
      documents2: documentsBase64.documents2 || (existingDocuments.documents2 && !formData.documents.documents2 ? "" : existingDocuments.documents2 || ""),
      documents3: documentsBase64.documents3 || (existingDocuments.documents3 && !formData.documents.documents3 ? "" : existingDocuments.documents3 || ""),
    };

    // Clean up null/undefined
    Object.keys(payload).forEach(key => {
      if (payload[key] === null || payload[key] === undefined) {
        delete payload[key];
      }
    });

    const url = mode === 'add'
      ? '/ems/tranfer/cases'  
      : `/ems/tranfer/cases/${formData.id}`;

    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${sessionStorage.getItem('auth_token') || sessionStorage.getItem('authToken')}`
      }
    };

    if (mode === 'edit') {
      payload._method = 'PUT';
    }

    await axiosInstance.post(url, payload, config);

    alert(mode === 'add' ? 'Transfer case created successfully!' : 'Transfer case updated!');
    refreshList();
    onHide(true);
  } catch (err) {
    console.error('Submission error:', err);
    const msg = err.response?.data?.message || err.message || 'Operation failed';
    alert(`Error: ${msg}`);
  } finally {
    setIsLoading(false);
  }
};

  const getDisplayFileName = (item) => {
    if (!item) return '';
    if (typeof item === 'string') return item.split('/').pop().split('?')[0];
    return item.name.length > 20 ? `${item.name.substring(0, 17)}...${item.name.split('.').pop()}` : item.name;
  };

  return (
    <>
      <Modal show={show} onHide={() => onHide(false)} size="lg" centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleSubmit}>
            <div className="row">
              {/* Name */}
              <div className="col-md-6 mb-3">
                <label className="form-label">Name <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter patient name"
                />
                {errors.name && <div className="invalid-feedback">{errors.name}</div>}
              </div>

              {/* Age */}
              <div className="col-md-6 mb-3">
                <label className="form-label">Age</label>
                <input type="number" className="form-control" name="age" value={formData.age} onChange={handleChange} min="0" max="120" />
              </div>

              {/* Gender */}
              <div className="col-md-6 mb-3">
                <label className="form-label">Gender</label>
                <select className="form-control form-select" name="gender" value={formData.gender} onChange={handleChange}>
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>

              {/* Transfer From */}
              <div className="col-md-6 mb-3">
                <label className="form-label">Transfer From <span className="text-danger">*</span></label>
                <select
                  className={`form-control form-select ${errors.transferFrom ? 'is-invalid' : ''}`}
                  value={transferFrom}
                  onChange={(e) => {
                    setTransferFrom(e.target.value);
                    setErrors(prev => ({ ...prev, transferFrom: '' }));
                    setFormData(prev => ({ ...prev, doctor: '', doctor_name: '' }));
                    setDoctors([]);
                  }}
                  disabled={['Hospital Representative (Nurse)', 'EMS Resource Manager'].includes(userRole) && medicalCenterId && medicalCenterId !== '0'}
                >
                  <option value="">Select Medical Center</option>
                  {medicalCenters.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                {errors.transferFrom && <div className="invalid-feedback">{errors.transferFrom}</div>}
              </div>

              {/* Transfer To */}
              <div className="col-md-6 mb-3">
                <label className="form-label">Transfer To <span className="text-danger">*</span></label>
                <select
                  className={`form-control form-select ${errors.transferTo ? 'is-invalid' : ''}`}
                  value={transferTo}
                  onChange={(e) => {
                    setTransferTo(e.target.value);
                    setErrors(prev => ({ ...prev, transferTo: '' }));
                  }}
                >
                  <option value="">Select Medical Center</option>
                  {filteredTransferTo.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                {errors.transferTo && <div className="invalid-feedback">{errors.transferTo}</div>}
              </div>

              {/* Diagnose */}
              <div className="col-md-6 mb-3">
                <label className="form-label">Diagnose <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className={`form-control ${errors.diagnose ? 'is-invalid' : ''}`}
                  name="diagnose"
                  value={formData.diagnose}
                  onChange={handleChange}
                />
                {errors.diagnose && <div className="invalid-feedback">{errors.diagnose}</div>}
              </div>

              {/* Doctor */}
              <div className="col-md-6 mb-3">
                <label className="form-label">Doctor <span className="text-danger">*</span></label>
                <select
                  className={`form-control form-select ${errors.doctor ? 'is-invalid' : ''}`}
                  value={formData.doctor}
                  onChange={handleDoctorChange}
                >
                  <option value="">Select Doctor</option>
                  {doctors.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
                {errors.doctor && <div className="invalid-feedback">{errors.doctor}</div>}
              </div>

              {/* Bracelet ID */}
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  Bracelet ID {mode === 'add' && <span className="text-danger">*</span>}
                </label>
                <div className="d-flex align-items-center">
                  <input
                    type="text"
                    className={`form-control ${errors.bracelet_id ? 'is-invalid' : ''}`}
                    name="bracelet_id"
                    value={formData.bracelet_id}
                    onChange={handleChange}
                  />
                  <i
                    className="ti ti-scan text-primary fs-4 ms-3 d-lg-none d-block"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setShowScanner(true)}
                  ></i>
                </div>
                {errors.bracelet_id && <div className="invalid-feedback">{errors.bracelet_id}</div>}
              </div>

              {/* Clinic No */}
              <div className="col-md-12 mb-3">
                <label className="form-label">Clinic No <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className={`form-control ${errors.clinic_no ? "is-invalid" : ""}`}
                  name="clinic_no"
                  value={formData.clinic_no}
                  onChange={handleChange}
                />
                {errors.clinic_no && <div className="invalid-feedback">{errors.clinic_no}</div>}
              </div>

              {/* Request Details */}
              <div className="col-md-12 mb-3">
                <label className="form-label">Request Details <span className="text-danger">*</span></label>
                <textarea
                  className={`form-control ${errors.request_details ? "is-invalid" : ""}`}
                  name="request_details"
                  value={formData.request_details}
                  onChange={handleChange}
                  rows="3"
                />
                {errors.request_details && <div className="invalid-feedback">{errors.request_details}</div>}
              </div>

              {/* Escort Team */}
              <div className="col-md-12 mb-3">
                <label className="form-label">Escort Team</label>
                <div className="row">
                  <div className="col-md-6 mb-2">
                    <input type="text" className="form-control" name="escort_physician" value={formData.escort_team.escort_physician} onChange={handleEscortTeamChange} placeholder="Escort Physician" />
                  </div>
                  <div className="col-md-6 mb-2">
                    <input type="text" className="form-control" name="escort_nurse" value={formData.escort_team.escort_nurse} onChange={handleEscortTeamChange} placeholder="Escort Nurse" />
                  </div>
                  <div className="col-md-6 mb-2">
                    <input type="text" className="form-control" name="escort_paramedic" value={formData.escort_team.escort_paramedic} onChange={handleEscortTeamChange} placeholder="Escort Paramedic" />
                  </div>
                  <div className="col-md-6 mb-2">
                    <input type="text" className="form-control" name="other" value={formData.escort_team.other} onChange={handleEscortTeamChange} placeholder="Other" />
                  </div>
                </div>
              </div>

              {/* Documents */}
              <div className="col-md-12 mb-3">
                <label className="form-label">
                  {mode === 'add' ? 'Attachment' : 'Documents Upload'} {mode === 'add' && <span className="text-danger">*</span>}
                </label>
                <small className="text-muted d-block mb-2">
                  Allowed file types: PDF, JPG, JPEG, PNG, WEBP, JFIF. Maximum 3 files.
                </small>
                {errors.documents && <div className="text-danger mb-2">{errors.documents}</div>}

                {fileInputs.map((_, index) => (
                  <div key={index} className="input-group mb-2">
                    <input
                      type="file"
                      className={`form-control ${errors[`documents${index + 1}`] ? 'is-invalid' : ''}`}
                      onChange={(e) => handleFileChange(e, index)}
                      accept="image/jpeg,image/png,image/webp,image/jfif,application/pdf"
                    />
                    {formData.documents[`documents${index + 1}`] && (
                      <OverlayTrigger placement="top" overlay={<Tooltip>{formData.documents[`documents${index + 1}`].name}</Tooltip>}>
                        <span className="input-group-text" style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {getDisplayFileName(formData.documents[`documents${index + 1}`])}
                        </span>
                      </OverlayTrigger>
                    )}
                    <div className="input-group-append d-flex align-items-center">
                      {index === fileInputs.length - 1 && fileInputs.length < MAX_FILES && (
                        <button type="button" className="btn btn-sm btn-icon fs-5 me-2" onClick={addFileInput}>
                          <i className="ti ti-circle-plus text-success"></i>
                        </button>
                      )}
                      {fileInputs.length > 1 && (
                        <button type="button" className="btn btn-sm btn-icon fs-5" onClick={() => removeFileInput(index)}>
                          <i className="ti ti-trash text-danger"></i>
                        </button>
                      )}
                    </div>
                    {errors[`documents${index + 1}`] && <div className="invalid-feedback">{errors[`documents${index + 1}`]}</div>}
                  </div>
                ))}
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-danger" onClick={() => onHide(false)} disabled={isLoading}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? <>Saving...</> : (mode === 'add' ? 'Save' : 'Update')}
          </Button>
        </Modal.Footer>
      </Modal>

      {showScanner && (
        <Modal show={showScanner} onHide={() => setShowScanner(false)} centered backdrop="static">
          <Modal.Header closeButton><Modal.Title>Scan QR Code</Modal.Title></Modal.Header>
          <Modal.Body><div id="reader" style={{ width: '100%', maxWidth: '400px', margin: '0 auto' }} /></Modal.Body>
        </Modal>
      )}
    </>
  );
};

export default TransferCaseModal;